// client/src/components/Parent/StudentAvatar.js
//
// A child's face, or their initials when the school has not added a photo.
//
// The photo is stored two ways depending on how it was added: an `/uploads/...`
// path, which has to be resolved against the API host because the web app is
// served from a different origin than the API, and an inline `data:` URI, which
// must be passed through untouched. Getting that wrong shows a broken image
// rather than falling back to initials, which looks like a fault in the record.
//
// The initials fallback is not decoration. A parent with three children reads
// this list by shape before they read it by name, and an identical grey circle
// on every row takes that away — so the colour is derived from the name and is
// therefore stable for a given child across sessions and devices.

import React from 'react';
import { Avatar } from '@mui/material';
// Face and Face3 are MUI's own boy/girl pair — the same two glyphs, drawn to
// the same weight and optical size, which a mixed icon pair never is.
import FaceIcon from '@mui/icons-material/Face';
import Face3Icon from '@mui/icons-material/Face3';
import { API_BASE } from '../../utils/api';

/** Two letters at most: first name, last name. */
export function initialsFor(first, last, fallback = '?') {
  const a = String(first || '').trim();
  const b = String(last || '').trim();
  const letters = `${a.charAt(0)}${b.charAt(0)}`.trim().toUpperCase();
  if (letters) return letters;
  const single = (a || b || String(fallback)).charAt(0).toUpperCase();
  return single || '?';
}

// A hue per name. Deterministic so the same child is the same colour every time;
// the saturation and lightness are fixed low enough to keep white text legible
// in either theme.
function tintFor(seed) {
  const s = String(seed || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${hash}, 45%, 42%)`;
}

/**
 * `/uploads/...` needs the API host in front of it; `data:` and `http:` do not.
 *
 * The trailing `/api` is stripped, deliberately. Uploads are served from the
 * server ROOT — `app.use('/uploads', express.static(...))` — not from under the
 * API prefix, so a deployment whose base URL ends in `/api` would otherwise ask
 * for `/api/uploads/...` and get a 404 that renders as a broken image rather
 * than falling back to initials.
 */
export function resolvePhoto(photoUrl) {
  const raw = String(photoUrl || '').trim();
  if (!raw) return null;
  if (/^(data:|https?:|blob:)/i.test(raw)) return raw;

  const base = String(API_BASE || '').replace(/\/$/, '').replace(/\/api$/i, '');
  const rel = raw.startsWith('/') ? raw : `/${raw}`;
  // No base configured means same-origin, which is what the browser does with a
  // relative path anyway.
  return base ? `${base}${rel}` : rel;
}

/**
 * A BOY OR A GIRL, WHERE THE SCHOOL HAS NOT ADDED A PHOTO.
 *
 * `sex` is an ENUM('M','F') and NOT NULL on the student record, so this is
 * known for every child in the school — which makes it better than initials at
 * the job the avatar is doing on a mark sheet. A teacher reading down a column
 * of thirty names is matching a row to a face they know; two letters in a
 * coloured circle help with that less than a figure that is at least the right
 * one of the two.
 *
 * The tint still comes from the name, so two girls in the same class are still
 * two different colours. The glyph says which, the colour says who.
 *
 * Anything other than M or F — a record written before the column existed, or
 * read from somewhere that did not send it — falls back to initials rather than
 * guessing.
 */
function sexGlyph(sex) {
  const v = String(sex || '').trim().toUpperCase();
  if (v === 'M' || v === 'MALE') return <FaceIcon />;
  if (v === 'F' || v === 'FEMALE') return <Face3Icon />;
  return null;
}

export default function StudentAvatar({ student, size = 40, sx }) {
  const first = student?.firstName || '';
  const last = student?.lastName || '';
  const name = `${first} ${last}`.trim() || String(student?.studentNumber || student?.id || '');
  const src = resolvePhoto(student?.photoUrl);
  const glyph = src ? null : sexGlyph(student?.sex ?? student?.gender);

  return (
    <Avatar
      src={src || undefined}
      alt={name}
      sx={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        fontWeight: 700,
        bgcolor: src ? undefined : tintFor(name),
        // The glyph is drawn at the icon's own size otherwise, which is 24px
        // whatever the disc is — far too large inside a 28px avatar on a mark
        // sheet and far too small inside a 64px one on a profile.
        '& .MuiSvgIcon-root': { fontSize: Math.round(size * 0.62) },
        ...sx,
      }}
    >
      {glyph || initialsFor(first, last, name)}
    </Avatar>
  );
}
